import React from 'react';
import styled from 'styled-components';

interface ListItemNode {
    level: number;
    content: string;
    children: ListItemNode[];
}

const StyledListItem = styled.li<{ level: number }>`
    padding-left: ${(props) => (props.level > 1 ? (props.level - 1) * 20 : 0)}px;
    list-style-position: inside;
    list-style-type: disc;
`;

const ListItem = ({ item }: { item: ListItemNode }) => {
    return (
        <StyledListItem level={item.level}>
            <span dangerouslySetInnerHTML={{ __html: item.content.replace(/\*\*|&/g, '') }} />
            {item.children.length > 0 && (
                <ListBox>
                    {item.children.map((child, index) => (
                        <ListItem key={index} item={child} />
                    ))}
                </ListBox>
            )}
        </StyledListItem>
    );
};

const processTextForDisplay = (text: string): string => {
    return text.replace(/&\*\*(.*?)\*&/g, '<strong>$1</strong>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

export const StringParser = (text: string): React.ReactNode => {
    if (!text) return null;

    const lines = text.split('\n');
    const isList = lines.some((line) => line.trim().startsWith('& -') || line.trim().startsWith('&-'));

    if (isList) {
        const structuredList: ListItemNode[] = [];
        const parentStack: ListItemNode[] = [];

        lines.forEach((line) => {
            const trimmedLine = line.trim();
            const match = trimmedLine.match(/^&(-+)\s*(.*)/);

            if (match) {
                const level = match[1].length;
                const content = processTextForDisplay(match[2]);
                const newNode: ListItemNode = { level, content, children: [] };

                while (parentStack.length > 0 && parentStack[parentStack.length - 1].level >= level) {
                    parentStack.pop();
                }

                if (parentStack.length > 0) {
                    parentStack[parentStack.length - 1].children.push(newNode);
                } else {
                    structuredList.push(newNode);
                }
                parentStack.push(newNode);
            }
        });

        return (
            <ListBox>
                {structuredList.map((item, index) => (
                    <ListItem key={index} item={item} />
                ))}
            </ListBox>
        );
    }

    const processedText = processTextForDisplay(text).replace(/-+/g, '•');
    return (
        <span
            style={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: processedText.replace(/\*\*|&/g, '') }}
        />
    );
};

const ListBox = styled.ul`
    margin: 0;
    padding-left: 0;
`;
